import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Section, SectionHeading } from '../common/Section';
import Reveal from '../common/Reveal';
import { WhatsAppLink } from '../common/WhatsAppButton';
import { SHOW_IMPACT_STATS, SHOW_TESTIMONIALS } from '../../config/claims';
import {
  SERVICE_CATEGORIES,
  SERVICES,
  findService,
  servicesByCategory,
} from '../../data/serviceCatalog';
import './Css/testimonials.css';

/**
 * The four testimonials exactly as they were written, kept verbatim so
 * nothing of the owner's is lost. They render only when
 * SHOW_TESTIMONIALS is switched on in src/config/claims.js.
 *
 * The `avatar` field is gone for good. Each one pointed at an Unsplash
 * photograph of a real, unrelated person being passed off as the
 * client; a flag cannot make that acceptable. `photo` replaces it and
 * accepts a *local* path only (see isLocalPhoto) — anything else falls
 * back to a monogram, so a remote stock URL cannot be dropped back in.
 */
export const TESTIMONIALS = [
  {
    id: 1,
    name: 'Sophia P.',
    role: 'Marketing Director',
    company: 'TechCorp',
    content:
      "DepthLogix transformed our digital presence. Their mobile app increased our customer engagement by 47% in just three months. The team's expertise in React Native and user experience design was exceptional.",
    photo: null,
    rating: 5,
    project: 'E-commerce Mobile App',
    duration: '3 Months',
    industry: 'Retail',
    metrics: ['47% Engagement Increase', '3-Month Delivery', '99.8% Uptime'],
  },
  {
    id: 2,
    name: 'Michael R.',
    role: 'CEO',
    company: 'StartUp Ventures',
    content:
      'The web application developed by DepthLogix has streamlined our operations and saved us countless hours of manual work. Their agile methodology ensured we got exactly what we needed.',
    photo: null,
    rating: 5,
    project: 'SaaS Platform',
    duration: '6 Months',
    industry: 'Technology',
    metrics: ['60% Time Saved', 'Scalable Architecture', '24/7 Support'],
  },
  {
    id: 3,
    name: 'Jennifer L.',
    role: 'Product Manager',
    company: 'Innovate Inc.',
    content:
      "Their team's attention to detail and commitment to quality is exceptional. They delivered ahead of schedule and exceeded our expectations in every aspect of the project.",
    photo: null,
    rating: 5,
    project: 'AI Analytics Dashboard',
    duration: '4 Months',
    industry: 'Analytics',
    metrics: ['Early Delivery', 'Zero Bugs', 'User-Friendly Design'],
  },
  {
    id: 4,
    name: 'David K.',
    role: 'CTO',
    company: 'FinTech Solutions',
    content:
      'The cloud infrastructure and DevOps practices implemented by DepthLogix improved our system reliability by 99.9%. Their technical expertise is truly world-class.',
    photo: null,
    rating: 5,
    project: 'Cloud Migration',
    duration: '5 Months',
    industry: 'Finance',
    metrics: ['99.9% Reliability', 'Cost Optimized', 'Secure Infrastructure'],
  },
];

/**
 * The "Our Impact" figures, kept verbatim, gated on SHOW_IMPACT_STATS.
 * The original data carried an emoji per row (rocket / star / chart /
 * shield); emoji-as-interface-icon is out under DESIGN.md §1, so the
 * numbers now carry themselves.
 */
export const ACHIEVEMENTS = [
  { number: '50+', label: 'Projects Delivered', description: 'Successful deployments' },
  { number: '98%', label: 'Client Satisfaction', description: 'Happy clients' },
  { number: '47%', label: 'Avg. Growth', description: 'Business impact' },
  { number: '24/7', label: 'Support', description: 'Always available' },
];

/**
 * Was declared and never rendered (the markup that used it had been
 * commented out). Exported rather than deleted so the list survives,
 * but it is not published: an industry list is a client claim too.
 */
export const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'E-commerce',
  'Education',
  'Startups',
];

/** Autoplay dwell. The CSS progress bar reads the same 5s, so the two
 *  cannot drift the way the old inline-style duration did. */
const SLIDE_MS = 5000;

/**
 * Real services, pulled from the catalog, chosen to span all four
 * practices and the full range of engagement shapes — a few weeks, a
 * few months, and an ongoing retainer. Every title, duration and URL
 * below comes from serviceCatalog.js; nothing is written by hand.
 */
const ENGAGEMENT_EXAMPLES = [
  ['digital-transformation', 'web-development'],
  ['emerging-tech', 'enterprise-ai-solutions'],
  ['business-apps', 'dynamics-365-crm'],
  ['digital-marketing', 'seo'],
  ['digital-marketing', 'maintenance-&-support'],
]
  .map(([categoryKey, slug]) => findService(categoryKey, slug))
  .filter(Boolean);

const CATEGORY_ROWS = SERVICE_CATEGORIES.map((category) => ({
  ...category,
  count: servicesByCategory(category.key).length,
}));

/** "Sophia P." → "SP". Used for the monogram that replaced the avatars. */
const initialsOf = (name) =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

/** Only a local asset counts as a real photograph. A remote URL is
 *  ignored on purpose — that is how the stock headshots got here. */
const isLocalPhoto = (src) => typeof src === 'string' && src.startsWith('/');

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(query.matches);
    sync();

    if (query.addEventListener) {
      query.addEventListener('change', sync);
      return () => query.removeEventListener('change', sync);
    }
    query.addListener(sync);
    return () => query.removeListener(sync);
  }, []);

  return reduced;
}

const Stars = ({ rating }) => (
  <span className="flex items-center gap-1">
    <span className="sr-only">{`Rated ${rating} out of 5`}</span>
    {Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        aria-hidden="true"
        className={`h-4 w-4 ${i < rating ? 'text-amber-500' : 'text-ink-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </span>
);

/** Initials in a brand-tinted circle, or a genuine local portrait. */
const ClientMark = ({ testimonial }) =>
  isLocalPhoto(testimonial.photo) ? (
    <img
      src={testimonial.photo}
      alt=""
      aria-hidden="true"
      width={56}
      height={56}
      loading="lazy"
      decoding="async"
      className="h-14 w-14 flex-none rounded-full object-cover ring-1 ring-brand-800/10"
    />
  ) : (
    <span
      aria-hidden="true"
      className="grid h-14 w-14 flex-none place-items-center rounded-full bg-brand-50 font-mono text-sm font-semibold text-brand-800 ring-1 ring-brand-800/10"
    >
      {initialsOf(testimonial.name)}
    </span>
  );

/**
 * Quote carousel. Only mounted when SHOW_TESTIMONIALS is true.
 *
 * Autoplay stops on hover, on keyboard focus, when the section scrolls
 * off screen, when the tab is hidden, on request via the pause button,
 * and entirely under prefers-reduced-motion. Slides share a single grid
 * cell so the panel height never changes mid-rotation and the crossfade
 * can actually run (the old markup used `hidden`, which cannot animate).
 */
const TestimonialCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const [runCycle, setRunCycle] = useState(0);
  const rootRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  const count = TESTIMONIALS.length;
  const running = !reduced && !paused && !interacting && onScreen && tabVisible;

  // Idle while off screen instead of rotating to an empty room.
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setOnScreen(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sync = () => setTabVisible(document.visibilityState !== 'hidden');
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  // One timeout per slide rather than a free-running interval, so a
  // manual jump restarts the dwell instead of cutting it short.
  useEffect(() => {
    if (!running) return undefined;
    const id = setTimeout(() => setIndex((prev) => (prev + 1) % count), SLIDE_MS);
    return () => clearTimeout(id);
  }, [running, index, count]);

  // Bumped only when the timer resumes. The progress bar is keyed on it
  // so it freezes in place on pause and restarts in step on resume.
  useEffect(() => {
    if (running) setRunCycle((cycle) => cycle + 1);
  }, [running]);

  const go = (next) => setIndex((next + count) % count);

  return (
    <div
      ref={rootRef}
      role="group"
      aria-roledescription="carousel"
      aria-label="Client testimonials"
      className="overflow-hidden rounded-panel border border-brand-800/10 bg-white shadow-card"
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      onFocus={() => setInteracting(true)}
      // Moving focus between the controls should not count as leaving.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false);
      }}
    >
      <div className="dl-progress-track" data-running={running ? 'true' : 'false'}>
        <span key={`${index}-${runCycle}`} className="dl-progress-bar block" />
      </div>

      <div
        className="dl-slides p-6 sm:p-8 lg:p-10"
        aria-live={running ? 'off' : 'polite'}
      >
        {TESTIMONIALS.map((testimonial, i) => (
          <figure
            key={testimonial.id}
            className="dl-slide m-0"
            data-active={i === index ? 'true' : 'false'}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <Stars rating={testimonial.rating} />
              <span className="inline-flex items-center rounded-full bg-brand-800 px-3 py-1 font-mono text-[0.68rem] uppercase tracking-eyebrow text-white">
                {testimonial.project}
              </span>
              <span className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-600">
                {testimonial.duration} · {testimonial.industry}
              </span>
            </div>

            <blockquote className="mt-6 max-w-[60ch] font-bricolage text-xl leading-snug text-ink-900 sm:text-2xl">
              {testimonial.content}
            </blockquote>

            <ul className="mt-6 flex flex-wrap gap-2">
              {testimonial.metrics.map((metric) => (
                <li
                  key={metric}
                  className="rounded-full border border-brand-100 bg-brand-50 px-3 py-1 font-mono text-[0.68rem] text-brand-800"
                >
                  {metric}
                </li>
              ))}
            </ul>

            <figcaption className="mt-8 flex items-center gap-4 border-t border-brand-800/10 pt-6">
              <ClientMark testimonial={testimonial} />
              <span className="min-w-0">
                <cite className="block font-semibold not-italic text-ink-900">
                  {testimonial.name}
                </cite>
                <span className="mt-0.5 block text-sm text-ink-600">
                  {testimonial.role}, {testimonial.company}
                </span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="flex flex-col gap-4 border-t border-brand-800/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="Previous testimonial"
            className="grid h-10 w-10 place-items-center rounded-full border border-ink-200 bg-white text-ink-700 transition-[color,border-color,background-color] duration-200 hover:border-brand-300 hover:text-brand-700"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="Next testimonial"
            className="grid h-10 w-10 place-items-center rounded-full border border-ink-200 bg-white text-ink-700 transition-[color,border-color,background-color] duration-200 hover:border-brand-300 hover:text-brand-700"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
          {/* WCAG 2.2.2 — an explicit, always-visible way to stop it. */}
          <button
            type="button"
            onClick={() => setPaused((was) => !was)}
            aria-pressed={paused}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-ink-200 bg-white px-4 font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-700 transition-[color,border-color] duration-200 hover:border-brand-300 hover:text-brand-700"
          >
            {paused ? (
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Pause className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {paused ? 'Play' : 'Pause'}
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            {TESTIMONIALS.map((testimonial, i) => (
              <button
                key={testimonial.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                className="grid h-6 place-items-center px-1"
              >
                <span
                  aria-hidden="true"
                  className={`block h-1 rounded-full transition-[width,background-color] duration-200 ${
                    i === index ? 'w-7 bg-brand-700' : 'w-3 bg-ink-300'
                  }`}
                />
              </button>
            ))}
          </div>
          <p className="font-mono text-xs text-ink-600">
            <span className="text-brand-700">{index + 1}</span>
            <span className="mx-1">/</span>
            <span>{count}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * The default state, and the one that ships: no invented clients, no
 * invented numbers. Everything here is read out of the real service
 * catalog — the titles, the delivery windows and the URLs are the same
 * ones published on the service pages.
 */
const EngagementPanel = () => (
  <div className="mt-14 grid items-start gap-10 lg:mt-20 lg:grid-cols-[0.92fr_1.08fr] lg:gap-16">
    <Reveal>
      <p className="font-mono text-[0.7rem] uppercase tracking-eyebrow text-brand-600">
        {SERVICES.length} services · {SERVICE_CATEGORIES.length} practices
      </p>
      <p className="mt-6 max-w-[54ch] text-base leading-relaxed text-ink-600 sm:text-lg">
        Scope, delivery window and the technologies involved are published on every
        service page before you speak to anyone. Describe what you&apos;re trying to
        build and we&apos;ll tell you which practice it lands in and how long that
        kind of work usually runs.
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link to="/contact" className="btn-primary">
          Start a conversation
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <WhatsAppLink
          variant="outline"
          message="Hi DepthLogix, I'd like to talk through the scope and timeline for a project. Could we set up a short call?"
        >
          Message us on WhatsApp
        </WhatsAppLink>
      </div>
    </Reveal>

    <Reveal delay={90}>
      <div className="grid-field overflow-hidden rounded-panel border border-brand-800/10 bg-white shadow-card">
        <div className="flex items-baseline justify-between gap-4 border-b border-brand-800/10 px-5 py-5 sm:px-7">
          <h3 className="font-bricolage text-lg font-semibold text-ink-900">
            Typical delivery windows
          </h3>
          <span className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-600">
            From the catalog
          </span>
        </div>

        <ul className="divide-y divide-brand-800/10">
          {ENGAGEMENT_EXAMPLES.map((service) => (
            <li key={service.path}>
              <Link
                to={service.path}
                className="group flex flex-col gap-1 px-5 py-4 transition-colors duration-200 hover:bg-brand-50/70 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6 sm:px-7"
              >
                <span className="min-w-0">
                  <span className="block font-medium text-ink-900 transition-colors duration-200 group-hover:text-brand-700">
                    {service.title}
                  </span>
                  <span className="mt-1 block font-mono text-[0.66rem] uppercase tracking-eyebrow text-ink-600">
                    {service.category}
                  </span>
                </span>
                <span className="flex flex-none items-center gap-1.5 font-mono text-xs text-brand-700">
                  {service.duration}
                  <ArrowUpRight
                    className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="border-t border-brand-800/10 px-5 py-4 sm:px-7">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 transition-colors duration-200 hover:text-brand-800"
          >
            See all {SERVICES.length} services
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </Reveal>
  </div>
);

const Testimonials = () => (
  <Section id="testimonials" tone="light">
    {SHOW_TESTIMONIALS ? (
      <SectionHeading
        eyebrow="Client Testimonials"
        title={
          <>
            Trusted by <span className="dl-gradient-text">Innovative Companies</span>
          </>
        }
        lead="Projects we delivered, described by the people we delivered them for."
        maxWidth="max-w-2xl"
      />
    ) : (
      <SectionHeading
        eyebrow="Working with us"
        title={
          <>
            How we <span className="dl-gradient-text">work with clients</span>
          </>
        }
        lead="Every engagement starts with a defined scope and a delivery window. These are the windows we plan against, taken straight from the service pages."
        maxWidth="max-w-2xl"
      />
    )}

    {SHOW_TESTIMONIALS ? (
      <Reveal className="mt-14 lg:mt-20">
        <TestimonialCarousel />
      </Reveal>
    ) : (
      <EngagementPanel />
    )}

    {/* The four real practices — a spec strip, not another card row. */}
    <Reveal delay={120} className="mt-16 lg:mt-20">
      <div className="rule" />
      <ul className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
        {CATEGORY_ROWS.map((category, i) => (
          <li
            key={category.key}
            className="lg:border-l lg:border-brand-800/10 lg:pl-7 lg:first:border-l-0 lg:first:pl-0"
          >
            <span className="font-mono text-[0.68rem] tracking-eyebrow text-brand-600">
              {String(i + 1).padStart(2, '0')}
            </span>
            <h3 className="mt-3 font-bricolage text-lg font-semibold text-ink-900">
              {category.label}
            </h3>
            <p className="mt-2 max-w-[42ch] text-sm leading-relaxed text-ink-600">
              {category.blurb}
            </p>
            <p className="mt-3 font-mono text-[0.66rem] uppercase tracking-eyebrow text-ink-600">
              {category.count} services
            </p>
          </li>
        ))}
      </ul>
    </Reveal>

    {SHOW_IMPACT_STATS ? (
      <Reveal delay={150} className="mt-16 lg:mt-20">
        <div className="rule" />
        <h3 className="mt-10 font-bricolage text-xl font-semibold text-ink-900">Our Impact</h3>
        <dl className="mt-8 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
          {ACHIEVEMENTS.map((achievement) => (
            <div
              key={achievement.label}
              className="lg:border-l lg:border-brand-800/10 lg:pl-7 lg:first:border-l-0 lg:first:pl-0"
            >
              <dt className="sr-only">{achievement.label}</dt>
              <dd>
                <span className="dl-gradient-text block font-bricolage text-4xl font-bold">
                  {achievement.number}
                </span>
                <span className="mt-3 block text-sm font-semibold text-ink-900">
                  {achievement.label}
                </span>
                <span className="mt-1 block text-sm text-ink-600">
                  {achievement.description}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </Reveal>
    ) : null}
  </Section>
);

export default Testimonials;
